import styled from '@emotion/styled';
import {Container, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H2} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {useCtaBlock} from 'UI/modules/Blocks/CtaBlock/useCtaBlock';

import type {CtaBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `CtaBlock` component.
 * This interface specifies all the required and optional properties that must be passed to render a CTA (Call-to-Action) block component.
 * It extends the Payload CMS CtaBlockType to ensure type safety and consistency with the backend data structure.
 */
interface ComponentProps {
    /**
     * The CTA button configuration and content for the block.
     * This property contains all the necessary information to render an interactive button element within the CTA block.
     */
    ctaButton: CtaBlockType['ctaButton'];
    /**
     * The descriptive text content that provides additional context for the CTA block.
     * This description appears below the title and helps users understand what action they should take or what they will achieve by interacting with the CTA.
     */
    description: CtaBlockType['description'];
    /**
     * The unique identifier for this specific CTA block instance.
     * This ID is typically generated by the CMS and used for tracking, analytics, and internal reference purposes.
     */
    id: CtaBlockType['id'];
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable automated tests.
     * It enables test frameworks like Cypress to locate and interact with this specific component instance.
     *
     * @default 'CtaBlock'
     */
    testId?: string;
    /**
     * The main heading text that serves as the primary call-to-action message.
     * This title is prominently displayed and should clearly communicate the value proposition or action the user should take.
     */
    title: CtaBlockType['title'];
}

/**
 * The CtaBlock component renders a call-to-action section with a title, description, and interactive button.
 * This component is designed to encourage user engagement by presenting a clear value proposition and actionable element in a visually appealing card format.
 * It utilizes MobX observer pattern for reactive updates and integrates with Payload CMS data structures to display dynamic content from the backend.
 *
 * @param props             The component props.
 * @param props.ctaButton   The CTA button configuration and content for the block.
 * @param props.description The descriptive text content that provides additional context for the CTA block.
 * @param props.id          The unique identifier for this specific CTA block instance.
 * @param props.testId      The unique identifier for testing purposes.
 * @param props.title       The main heading text that serves as the primary call-to-action message.
 * @returns The rendered CTA block component with title, description, and interactive button.
 *
 * @example
 * ```tsx
 * <CtaBlock
 *     ctaButton={{
 *         text: 'Get Started',
 *         url: '/signup',
 *         target: '_self'
 *     }}
 *     description={[{
 *         type: 'paragraph',
 *         children: [{text: 'Join thousands of users today'}]
 *     }]}
 *     id="cta-block-1"
 *     title="Ready to Begin?"
 *     testId="homepage-cta"
 * />
 * ```
 */
const CtaBlock = observer(({ctaButton, description, id, testId = 'CtaBlock', title}: ComponentProps) => {
    const {ctaHref, ctaRel, ctaTarget, ctaText} = useCtaBlock(ctaButton);

    return (
        <Container data-cy={testId}>
            <ViewportTrigger baseComponent="div">
                <Wrapper variants={FadeInAnimation}>
                    <MotionH2 id={id ?? ''} variants={FadeInAnimation}>{title}</MotionH2>
                    <Spacer y={8} isNotStretching />
                    <motion.div variants={FadeInAnimation}>
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                        {description && <PayloadLexical description={description} />}
                    </motion.div>
                    <Spacer y={8} isNotStretching />
                    <motion.div variants={FadeInAnimation}>
                        <Button
                            as="link"
                            href={ctaHref}
                            rel={ctaRel}
                            target={ctaTarget}
                        >
                            {ctaText}
                        </Button>
                    </motion.div>
                </Wrapper>
            </ViewportTrigger>
        </Container>
    );
});

CtaBlock.displayName = 'CtaBlock';

export {CtaBlock};

const Wrapper = styled(motion.div)`
    align-items: center;
    background-color: ${({theme}) => theme.colors.secondaryDark};
    border-radius: 16px;
    color: ${({theme}) => theme.colors.textLight};
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${spacing(22)};
    text-align: center;
`;

const MotionH2 = H2.withComponent(motion.create(H2));